"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase/client";
import { isSupabaseConfigured } from "@/lib/supabase/env";
import { captureReliable } from "@/lib/analytics/events";

/**
 * Header call-to-action, auth-aware on the client so the rest of the layout
 * stays statically renderable. Signed-out visitors get Log in + Sign up (styled
 * apart from the blue portfolio CTA); signed-in users get their dashboard.
 */
export function HeaderCta() {
  const [authed, setAuthed] = useState<boolean | null>(null);

  useEffect(() => {
    // Every other Supabase call site in this repo guards with
    // isSupabaseConfigured() first (see src/lib/supabase/env.ts) — this one
    // didn't, so an unconfigured environment would throw createClient()'s
    // "Supabase is not configured" error on every page load, in every
    // visitor's browser, since this component sits in the root header.
    if (!isSupabaseConfigured()) {
      setAuthed(false);
      return;
    }
    const sb = createClient();
    sb.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = sb.auth.onAuthStateChange((_e, session) =>
      setAuthed(!!session),
    );
    return () => sub.subscription.unsubscribe();
  }, []);

  if (authed) {
    return (
      <div className="ml-1 flex shrink-0 items-center gap-0.5 sm:ml-2 sm:gap-2">
        <Link
          href="/my-jobs"
          className="telex rounded-md px-1.5 py-2 text-[10.5px] text-ink-mute transition-colors hover:bg-neutral-100 hover:text-ink sm:px-3"
        >
          My jobs
        </Link>
        <Link
          href="/dashboard"
          className="inline-flex h-9 items-center whitespace-nowrap rounded-md bg-brand-600 px-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-700 sm:px-4"
        >
          <span className="sm:hidden">My page</span>
          <span className="hidden sm:inline">Create your page</span>
        </Link>
      </div>
    );
  }

  // Signed out (and while the session is still loading — the common case).
  //
  // `captureReliable` (sendBeacon, send_instantly), not the plain `capture`:
  // confirmed live 2026-09-09 that header_login/header_signup together
  // logged only 3 login_cta_click events in 30 days against 31 real
  // /auth/login pageviews in 7 days alone — the same plain-capture-races-
  // navigation loss already fixed for search_tabs (ddaf833) and
  // post_apply_survey_response, just never applied here.
  return (
    <div className="ml-1 flex shrink-0 items-center gap-1 sm:ml-2 sm:gap-2">
      <Link
        href="/auth/login"
        rel="nofollow"
        onClick={() => captureReliable("login_cta_click", { surface: "header_login" })}
        className="inline-flex h-9 items-center whitespace-nowrap rounded-md px-2.5 text-[13px] font-semibold text-ink transition-colors hover:bg-neutral-100 sm:px-3"
      >
        Log in
      </Link>
      <Link
        href="/auth/login?next=/dashboard"
        rel="nofollow"
        onClick={() => captureReliable("login_cta_click", { surface: "header_signup" })}
        className="inline-flex h-9 items-center whitespace-nowrap rounded-md bg-ink px-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-ink/85 sm:px-4"
      >
        <span className="sm:hidden">Sign up</span>
        <span className="hidden sm:inline">Create your page</span>
      </Link>
    </div>
  );
}
